import React, { useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ModalCloseButton } from '../ModalCloseButton/ModalCloseButton';
import { getYouTubeEmbedUrl } from '../../utils/youtube';
import { useDialog } from '../../utils/useDialog';
import './VideoModal.scss';

export interface VideoModalProps {
  /** Video source URL: a YouTube watch/share/embed link plays in an embedded player, anything else in a native video element */
  src: string;
  /** Callback when modal is closed (close button, backdrop click, or Escape) */
  onClose: () => void;
  /** Optional heading shown above the player; also names the dialog and the embedded player */
  title?: string;
  /** Element focused when the modal closes; defaults to whatever had focus when it opened */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

export const VideoModal: React.FC<VideoModalProps> = ({ src, onClose, title, returnFocusRef }) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const embedUrl = getYouTubeEmbedUrl(src);
  const playerTitle = title || 'Video player';

  useDialog({
    containerRef: rootRef,
    onEscape: onClose,
    initialFocusRef: closeRef,
    restoreFocus: true,
    returnFocusRef,
    lockScroll: true,
    focusableSelector: 'button, video, iframe, [tabindex]:not([tabindex="-1"]):not([data-focus-guard])',
  });

  return createPortal(
    <div ref={rootRef} className="video-modal">
      <div
        className="video-modal__overlay"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="video-modal__container"
        role="dialog"
        aria-modal="true"
        {...(title ? { 'aria-labelledby': titleId } : { 'aria-label': playerTitle })}
      >
        <div className="video-modal__topbar">
          {title && (
            <h2 id={titleId} className="video-modal__title">
              {title}
            </h2>
          )}
          <ModalCloseButton
            buttonRef={closeRef}
            className="video-modal__close"
            surface="dark"
            onClick={onClose}
            ariaLabel="Close video"
          />
        </div>
        {embedUrl ? (
          <>
            <iframe
              className="video-modal__player"
              src={embedUrl}
              title={playerTitle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {/* Key presses inside the cross-origin player never reach the document listener, so
                tabbing past its last control lands here and is sent back to the close button. */}
            <span
              tabIndex={0}
              data-focus-guard=""
              onFocus={() => closeRef.current?.focus()}
            />
          </>
        ) : (
          <video controls autoPlay tabIndex={0} className="video-modal__player">
            <source src={src} />
          </video>
        )}
      </div>
    </div>,
    document.body,
  );
};
